import React, { useState, useEffect } from "react";
import axios from "axios";

import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ROLE_BASED_API } from "../../api/api";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import "./SignUp.css";

import signupImg from "../../assets/loginpage-image.png";
import logo from "../../assets/Skilledition-logo.png";

const SignUp = () => {
  const [signup, setSignup] = useState({
    fullName: "",
    email: "",
    otp: "",
    phone: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loadingSignUp, setSignUpLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  const navigate = useNavigate();
  const role = localStorage.getItem("userRole");

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    if (!role) {
      showErrorToast("Please choose a role before sign up");
      const timer = setTimeout(() => {
        navigate("/choose-role");
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [role, navigate]);

  // Reset OTP if email changes
  useEffect(() => {
    setOtpSent(false);
    setOtpExpired(false);
    setOtpTimer(0);
  }, [signup.email]);

  // Countdown timer
  useEffect(() => {
    if (otpTimer <= 0) {
      if (otpSent) {
        setOtpExpired(true);
        setOtpSent(false);
      }
      return;
    }

    const interval = setInterval(() => {
      setOtpTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [otpTimer, otpSent]);

  // const formatTime = (seconds) => {
  //   const m = Math.floor(seconds / 60);
  //   const s = seconds % 60;
  //   return `${m}:${s < 10 ? "0" : ""}${s}`;
  // };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSignup((prev) => ({ ...prev, [name]: value }));
  };

  const togglePassword = () => setShowPassword((prev) => !prev);

  const handleSendOtp = async () => {
    if (otpLoading) return;

    if (!signup.email) {
      showErrorToast("Please enter email first");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(ROLE_BASED_API.SEND_OTP, {
        email: signup.email,
      });

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        setOtpSent(true);
        setOtpExpired(false);
        setOtpTimer(300);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Otp Error:", error);
      // Server responded with non-2xx status (like 400, 500)

      if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else {
        showErrorToast("Network error! Please try again.");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();

    if (!/^[a-zA-Z ]+$/.test(signup.fullName)) {
      showErrorToast("Full Name is required alphabets only.");

      return;
    }

    if (!otpSent) {
      showErrorToast("Please click Get OTP first");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(signup.phone)) {
      showErrorToast("Enter a valid 10-digit phone number.");
      return;
    }

    if (!/^\d{6}$/.test(signup.otp)) {
      showErrorToast("Otp should be 6 digits.");
      return;
    }

    const payload = {
      full_name: signup.fullName,
      email: signup.email,
      otp: signup.otp,
      phone_number: signup.phone,
      password: signup.password,
      role: role,
    };

    setSignUpLoading(true);
    try {
      const response = await axios.post(
        ROLE_BASED_API.ROLE_BASED_SIGNUP,
        payload,
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });
        setTimeout(() => {
          navigate("/signin");
        }, 1500);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Signup Error:", error);
      // Server responded with non-2xx status (like 409)
      if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else {
        // Network error or server didn't respond
        showErrorToast("Network error! Please try again.");
      }
    } finally {
      setSignUpLoading(false);
    }
  };

  return (
    <section className="student-signup-section">
      <Container className="student-signup-container">
        <Row className="student-signup-row">
          {/* Left Side - Signup Form */}
          <Col xs={12} md={6} className="student-signup-left">
            <div className="student-signup-content">
              <div className="student-signup-logo mb-4">
                <img src={logo} alt="Skilledition Logo" />
              </div>
              <h5 className="student-signup-title">Create Your Account</h5>
              <p className="student-signup-subtitle mb-3">
                Join Skilledition and start your learning journey today.
              </p>

              <Form
                className="student-signup-form"
                onSubmit={handleSignupSubmit}
              >
                <Form.Group
                  controlId="fullName"
                  className="student-signup-form-group"
                >
                  {/* <Form.Label className="student-signup-label">
                    Full Name
                  </Form.Label> */}
                  <Form.Control
                    type="text"
                    name="fullName"
                    placeholder="Enter your full name"
                    className="student-signup-input"
                    value={signup.fullName}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group
                  controlId="email"
                  className="student-signup-form-group"
                >
                  {/* <Form.Label className="student-signup-label">
                    Email
                  </Form.Label> */}
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    className="student-signup-input"
                    value={signup.email}
                    onChange={handleChange}
                    required
                    pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}"
                    title="Please enter a valid email address."
                  />

                  <span
                    onClick={
                      !otpLoading && (!otpSent || otpExpired)
                        ? handleSendOtp
                        : undefined
                    }
                    className="student-signup-get-otp-text"
                  >
                    {otpLoading
                      ? "Sending..."
                      : otpExpired
                        ? "Resend OTP"
                        : otpSent
                          ? "OTP sent successfully"
                          : "Get OTP"}
                  </span>
                </Form.Group>

                {otpSent && (
                  <>
                    <Form.Group
                      controlId="otp"
                      className="student-signup-form-group"
                    >
                      {/* <Form.Label className="student-signup-label">OTP</Form.Label> */}
                      <Form.Control
                        type="text"
                        name="otp"
                        placeholder="Enter OTP"
                        className="student-signup-input"
                        value={signup.otp}
                        onChange={handleChange}
                        maxLength={6}
                        required
                      />
                    </Form.Group>

                    <Form.Group
                      controlId="phone"
                      className="student-signup-form-group"
                    >
                      {/* <Form.Label className="student-signup-label">
                    Phone Number
                  </Form.Label> */}
                      <Form.Control
                        type="text"
                        name="phone"
                        placeholder="Enter your phone number"
                        className="student-signup-input"
                        value={signup.phone}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group
                      controlId="password"
                      className="student-signup-form-group"
                    >
                      {/* <Form.Label className="student-signup-label">
                    Password
                  </Form.Label> */}

                      <div className="student-signup-password-input-wrapper">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="Enter your password"
                          className="student-signup-input"
                          value={signup.password}
                          onChange={handleChange}
                          required
                          pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}"
                          title="Min 8 chars with uppercase, lowercase, number & symbol"
                        />
                        <span
                          className="student-signup-password-toggle-icon"
                          onClick={togglePassword}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      </div>
                    </Form.Group>
                  </>
                )}

                <Form.Group className="student-signup-form-group mb-3">
                  <Form.Check
                    type="checkbox"
                    id="termsCheckbox"
                    className="student-signup-checkbox"
                    label={
                      <span className="student-signup-checkbox-label">
                        I agree to the{" "}
                        <Link to="/privacy" className="student-signup-link">
                          Privacy Policy
                        </Link>{" "}
                        and
                        <Link to="/terms" className="student-signup-link">
                          Terms & Conditions
                        </Link>
                      </span>
                    }
                    required
                  />
                </Form.Group>

                <Button
                  type="submit"
                  className="student-signup-btn mt-2"
                  disabled={
                    loadingSignUp ||
                    !otpSent ||
                    !signup.otp.trim() ||
                    !signup.phone.trim() ||
                    !signup.password.trim()
                  }
                >
                  {loadingSignUp ? "Signing up..." : "Sign Up"}
                </Button>

                <div className="student-signup-login-link mt-3">
                  <p>
                    Already have an account?{" "}
                    <Link to="/signin" className="student-signup-login-anchor">
                      Sign In
                    </Link>
                  </p>
                </div>
              </Form>
              {/* <div className="student-signup-footer">
                © 2025 Skilledition. All rights reserved.
              </div> */}
            </div>
          </Col>

          {/* Right Side - Image Section */}
          <Col xs={12} md={6} className="student-signup-right d-none d-md-flex">
            <div className="student-signup-right-content">
              <h2 className="student-signup-right-title">
                The Easiest Way to Learn New Skills
              </h2>
              <p className="student-signup-right-desc">
                Learn, practice, and grow at your own pace
              </p>
              <img
                src={signupImg}
                alt="Signup Visual"
                className="student-signup-image"
              />
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default SignUp;
